// Capa de sesión JWT real, paralela al token invitado (ver CLAUDE.md § Sesión
// JWT / token invitado y docs/specs/sesion-jwt.md). Módulo puro (sin
// React/Zustand/Sentry): mismo patrón y misma prohibición de mockearlo en los
// tests de sus consumidores que src/lib/oneclickCards.js, paymentGuard.js,
// mpCheckout.js, transbankCredenciales.js.
//
// Responsabilidad única: dueño exclusivo de las claves localStorage del par
// accessToken/refreshToken de sesión, y clasificador de qué hacer ante una
// respuesta que huele a "token rechazado" (CA-14). `ApiCore.js` es el único
// consumidor previsto — nunca reemplaza `localStorage["token"]` (token
// invitado, dueño exclusivo: `postNoAuth` en ApiCore.js), ver CA-1.
//
// Kill-switch (mismo criterio que ONECLICK_FILTRO_ACTIVO / PAYMENT_LOCK_ACTIVO):
// en `false`, el sistema completo de sesión queda inerte (haySesion() siempre
// `false`, leerAccessToken() siempre `null`) y ApiCore degrada íntegramente al
// comportamiento previo con token invitado. Sirve para diagnóstico en QA, no
// como estrategia de rollback de producción (el deploy de esta SPA es
// inmediato y sin versiones antiguas instaladas).
import { decodeToken } from "react-jwt";
import { normalizarRut } from "./index";

export const SESION_JWT_ACTIVA = true;

export const SESION_ACCESS_KEY = "sima_access_token";
export const SESION_REFRESH_KEY = "sima_refresh_token";

export const REFRESH_URL = "/apiV2/usuario/refreshToken";

// Qué Authorization lleva la llamada a refreshToken (CA-34/S3, pregunta
// abierta N2 del plan): por defecto, el token invitado ("token" en
// localStorage) — nunca el access token de sesión, que en el escenario que
// dispara el refresh está justamente vencido/rechazado. Verificar contra QA
// real antes de confiar en este valor en producción.
export const REFRESH_AUTHORIZATION = "invitado";

// statusMessage que el backend puede devolver en un 403 (o body 200 "laxo")
// que NO son de sesión expirada — ver CA-14. Tiene precedencia sobre el
// regex de texto heredado (que sigue viviendo en ApiCore.js como fallback).
export const STATUS_NO_REFRESCABLES = [
  "IDENTIDAD_NO_COINCIDE",
  "TOKEN_PUBLICO_NO_AUTORIZADO",
  "PEDIDO_NO_AUTORIZADO",
];

// statusMessage confirmados por el backend como "token de sesión expirado".
// Vacío hasta que se responda PA-12 (docs/specs/migracion-login-jwt.md) — con
// la lista vacía, clasificarRespuestaAuth cae al regex heredado cuando no hay
// statusMessage, que es exactamente el comportamiento actual.
export const STATUS_EXPIRADOS = [];

// ---------------------------------------------------------------------------
// Storage (todo envuelto en try/catch: modo privado / cuota llena no debe
// nunca lanzar una excepción no capturada — caso borde 2 del documento de
// requerimientos, mismo precedente que ModalCredential.jsx).
// ---------------------------------------------------------------------------

const leerStorage = (key) => {
  try {
    const value = localStorage.getItem(key);
    return typeof value === "string" && value.length > 0 ? value : null;
  } catch {
    return null;
  }
};

const escribirStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Modo privado / quota llena: la sesión no persiste entre recargas, pero
    // no debe romper el flujo de login en curso.
  }
};

const borrarStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // no-op
  }
};

/** leerAccessToken() -> string | null. `null` si el kill-switch está apagado. */
export const leerAccessToken = () => {
  if (!SESION_JWT_ACTIVA) return null;
  return leerStorage(SESION_ACCESS_KEY);
};

/** leerRefreshToken() -> string | null. `null` si el kill-switch está apagado. */
export const leerRefreshToken = () => {
  if (!SESION_JWT_ACTIVA) return null;
  return leerStorage(SESION_REFRESH_KEY);
};

/**
 * guardarSesion({accessToken, refreshToken})
 * Persiste ambos tokens. No valida forma (JWT válido o no): eso es
 * responsabilidad del backend/consumidor.
 */
export const guardarSesion = ({ accessToken, refreshToken } = {}) => {
  if (typeof accessToken === "string" && accessToken) {
    escribirStorage(SESION_ACCESS_KEY, accessToken);
  }
  if (typeof refreshToken === "string" && refreshToken) {
    escribirStorage(SESION_REFRESH_KEY, refreshToken);
  }
};

/**
 * guardarAccessToken(token)
 * Actualiza SOLO el access token (CA-13: el refresh nunca se rota ni se
 * borra por un refresh exitoso que no trae uno nuevo).
 */
export const guardarAccessToken = (token) => {
  if (typeof token === "string" && token) {
    escribirStorage(SESION_ACCESS_KEY, token);
  }
};

/** limpiarSesion() — borra ambas claves de sesión (nunca "token"/"rut" del invitado). */
export const limpiarSesion = () => {
  borrarStorage(SESION_ACCESS_KEY);
  borrarStorage(SESION_REFRESH_KEY);
};

/**
 * haySesion()
 * `true` solo si hay access Y refresh no vacíos. Un refresh ausente o
 * corrupto se trata como "no hay sesión" (caso borde 3): degradar a
 * invitado es siempre más seguro que quedar en un estado a medias.
 */
export const haySesion = () => {
  if (!SESION_JWT_ACTIVA) return false;
  return Boolean(leerAccessToken() && leerRefreshToken());
};

// ---------------------------------------------------------------------------
// Identidad: derivar el header `rut` de la claim del access token (CA-6).
// ---------------------------------------------------------------------------

// Reutiliza la normalización Unicode que vivía privada en ModalCredential.jsx
// (guiones Unicode -> ASCII, invisibles fuera, cleanRut) — ahora exportada
// públicamente desde src/lib/index.js.

// Memoización de 1 entrada: decodeToken se invoca en cada request autenticada
// (vía ApiCore._headers), pero el access token solo cambia en login/refresh.
let cacheTokenDecodificado = { token: null, rut: "" };

/**
 * rutDeSesion(accessToken)
 * Decodifica (solo lectura, JAMÁS validación de firma) el claim `rut` del
 * access token y lo normaliza a formato `NNNNNNN-D`. `""` ante token vacío,
 * corrupto, sin claim `rut`, o si decodeToken lanza.
 */
export const rutDeSesion = (accessToken) => {
  if (!accessToken || typeof accessToken !== "string") return "";

  if (cacheTokenDecodificado.token === accessToken) {
    return cacheTokenDecodificado.rut;
  }

  let rut = "";
  try {
    // decodeToken puede lanzar con un JWT malformado.
    const payload = decodeToken(accessToken);
    const claim = payload && (payload.rut || payload.usuario_rut);
    rut = claim ? normalizarRut(String(claim)) : "";
  } catch {
    rut = "";
  }

  cacheTokenDecodificado = { token: accessToken, rut };
  return rut;
};

// Solo para tests: limpia la memoización de 1 entrada entre casos.
export const resetCacheRutDeSesion = () => {
  cacheTokenDecodificado = { token: null, rut: "" };
};

// ---------------------------------------------------------------------------
// Clasificación de respuestas 403 / body-200 "laxo" (CA-14).
// ---------------------------------------------------------------------------

// Regex heredado de ApiCore.js (_isExpiredTokenError), movido tal cual: sigue
// siendo el fallback cuando no viene `statusMessage`.
const REGEX_TOKEN_EXPIRADO = (data) => {
  const message =
    typeof data === "string" ? data : (data && (data.message || data.msg)) || "";
  return typeof message === "string" && /token/i.test(message) && /(inv[aá]lido|expirado)/i.test(message);
};

/**
 * clasificarRespuestaAuth(body) -> "no-refrescable" | "expirada" | "otro"
 * Precedencia (CA-14): `body.statusMessage` gana siempre que esté presente,
 * conocido o no — un statusMessage desconocido es información (no cae al
 * regex). Sin `statusMessage`, se evalúa el regex heredado como fallback.
 */
export const clasificarRespuestaAuth = (body) => {
  const statusMessage = body && typeof body === "object" ? body.statusMessage : undefined;

  if (typeof statusMessage === "string" && statusMessage) {
    if (STATUS_NO_REFRESCABLES.includes(statusMessage)) return "no-refrescable";
    if (STATUS_EXPIRADOS.includes(statusMessage)) return "expirada";
    return "otro";
  }

  return REGEX_TOKEN_EXPIRADO(body) ? "expirada" : "otro";
};
